import { useState, useMemo } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Calendar, Filter, RefreshCw, Download, Shield, TrendingUp, Clock, AlertCircle } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { complianceService } from '../services/compliance'
import { toast } from 'react-hot-toast'

export default function CompliancePage() {
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedProject] = useState<string>('all')
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all')
  const [loading, setLoading] = useState(false)

  // Fetch compliance checks
  const { data: complianceChecks, isLoading, error, refetch } = useQuery({
    queryKey: ['compliance-checks', selectedProject, selectedStatus],
    queryFn: async () => {
      const projectId = selectedProject === 'all' ? undefined : selectedProject
      const status = selectedStatus === 'all' ? undefined : selectedStatus
      return await complianceService.getComplianceChecks(projectId, status)
    },
    refetchInterval: 60000, // Refresh every minute
  })

  // Filter checks by severity
  const filteredChecks = useMemo(() => {
    if (!complianceChecks) return []
    if (selectedSeverity === 'all') return complianceChecks
    return complianceChecks.filter(check => check.severity === selectedSeverity)
  }, [complianceChecks, selectedSeverity])

  // Calculate statistics
  const stats = useMemo(() => {
    if (!complianceChecks) return {
      compliant: 0,
      nonCompliant: 0,
      pending: 0,
      overallScore: 0,
      criticalIssues: 0,
      upcomingDeadlines: 0
    }

    const compliant = complianceChecks.filter(c => c.status === 'compliant').length
    const nonCompliant = complianceChecks.filter(c => c.status === 'non_compliant').length
    const pending = complianceChecks.filter(c => c.status === 'pending' || c.status === 'in_progress').length
    const total = complianceChecks.length
    const overallScore = total > 0 ? Math.round((compliant / total) * 100) : 0

    const criticalIssues = complianceChecks.filter(
      c => c.severity === 'critical' && c.status === 'non_compliant'
    ).length

    const today = new Date()
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
    const upcomingDeadlines = complianceChecks.filter(c => {
      if (!c.due_date) return false
      const dueDate = new Date(c.due_date)
      return dueDate >= today && dueDate <= nextWeek
    }).length

    return {
      compliant,
      nonCompliant,
      pending,
      overallScore,
      criticalIssues,
      upcomingDeadlines
    }
  }, [complianceChecks])

  // Prepare data for pie chart
  const pieData = [
    { name: 'Compliant', value: stats.compliant, color: '#10b981' },
    { name: 'Non-Compliant', value: stats.nonCompliant, color: '#ef4444' },
    { name: 'Pending', value: stats.pending, color: '#f59e0b' },
  ]

  // Prepare data for severity distribution
  const severityData = useMemo(() => {
    if (!complianceChecks) return []
    const distribution = {
      low: 0,
      medium: 0,
      high: 0,
      critical: 0
    }
    complianceChecks.forEach(check => {
      if (check.severity) {
        distribution[check.severity]++
      }
    })
    return [
      { severity: 'Low', count: distribution.low, color: '#3b82f6' },
      { severity: 'Medium', count: distribution.medium, color: '#f59e0b' },
      { severity: 'High', count: distribution.high, color: '#ef4444' },
      { severity: 'Critical', count: distribution.critical, color: '#7c3aed' },
    ]
  }, [complianceChecks])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'pending':
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-500" />
      case 'non_compliant':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'waived':
        return <AlertTriangle className="h-5 w-5 text-gray-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-100 text-green-800'
      case 'pending':
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'non_compliant':
        return 'bg-red-100 text-red-800'
      case 'waived':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':
        return 'bg-blue-100 text-blue-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'critical':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleRunComplianceCheck = async () => {
    if (!selectedProject || selectedProject === 'all') {
      toast.error('Please select a specific project to run compliance check')
      return
    }

    try {
      setLoading(true)
      await complianceService.checkProjectCompliance(selectedProject)
      toast.success('Compliance check initiated successfully!')

      // Refresh the data
      setTimeout(() => {
        refetch()
      }, 1500)
    } catch (error) {
      console.error('Failed to run compliance check:', error)
      toast.error('Failed to run compliance check. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleExportReport = () => {
    if (!filteredChecks || filteredChecks.length === 0) {
      toast.error('No compliance checks to export')
      return
    }

    try {
      // Create CSV content
      const headers = ['Check Type', 'Status', 'Severity', 'Due Date', 'Completed Date', 'Notes']
      const csvRows = [
        headers.join(','),
        ...filteredChecks.map(check => [
          `"${check.check_type || ''}"`,
          `"${check.status || ''}"`,
          `"${check.severity || ''}"`,
          check.due_date || '',
          check.completed_date || '',
          `"${(check.notes || '').replace(/"/g, '""')}"` // Escape quotes in notes
        ].join(','))
      ]

      // Add summary statistics
      csvRows.push('')
      csvRows.push('Summary Statistics')
      csvRows.push(`Total Checks,${complianceChecks?.length || 0}`)
      csvRows.push(`Compliant,${stats.compliant}`)
      csvRows.push(`Non-Compliant,${stats.nonCompliant}`)
      csvRows.push(`Pending,${stats.pending}`)
      csvRows.push(`Overall Score,${stats.overallScore}%`)
      csvRows.push(`Critical Issues,${stats.criticalIssues}`)

      const csvContent = csvRows.join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `compliance-report-${Date.now()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      toast.success(`Exported ${filteredChecks.length} compliance checks successfully!`)
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Failed to export compliance report. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-900 font-semibold">Failed to load compliance data</p>
          <button 
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Compliance Management</h1>
            <p className="mt-2 text-gray-600">Monitor and maintain legal compliance across all projects</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            <button
              onClick={handleExportReport}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </button>
            <button
              onClick={handleRunComplianceCheck}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shield className="h-4 w-4 mr-2" />
              {loading ? 'Running...' : 'Run Compliance Check'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overall Score</p>
              <p className="text-2xl font-bold mt-1 text-primary-600">{stats.overallScore}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-primary-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliant</p>
              <p className="text-2xl font-bold mt-1 text-green-600">{stats.compliant}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Non-Compliant</p>
              <p className="text-2xl font-bold mt-1 text-red-600">{stats.nonCompliant}</p>
            </div>
            <XCircle className="h-8 w-8 text-red-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold mt-1 text-yellow-600">{stats.pending}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Critical Issues</p>
              <p className="text-2xl font-bold mt-1 text-purple-600">{stats.criticalIssues}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-purple-200" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Due This Week</p>
              <p className="text-2xl font-bold mt-1 text-blue-600">{stats.upcomingDeadlines}</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-200" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status Distribution</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Severity Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Items by Severity</h2>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={severityData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="severity" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6">
                {severityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Compliance Items</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Filter className="h-4 w-4 text-gray-400 mr-2" />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="border-gray-300 rounded-md text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="compliant">Compliant</option>
                  <option value="non_compliant">Non-Compliant</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="waived">Waived</option>
                </select>
              </div>
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Severities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>

        {/* Compliance Items List */}
        <ul className="divide-y divide-gray-200">
          {filteredChecks.length === 0 ? (
            <li className="px-4 py-8 text-center text-gray-500">
              No compliance items found
            </li>
          ) : (
            filteredChecks.map((item) => (
              <li key={item.id} className="hover:bg-gray-50 transition-colors">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      {getStatusIcon(item.status)}
                      <div className="ml-4 flex-1">
                        <div className="flex items-center">
                          <p className="text-sm font-medium text-gray-900">{item.check_type}</p>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(item.status)}`}>
                            {item.status.replace('_', ' ')}
                          </span>
                          <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSeverityColor(item.severity)}`}>
                            {item.severity}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          {item.due_date && (
                            <>
                              <Calendar className="h-4 w-4 mr-1" />
                              <span className="mr-4">Due: {new Date(item.due_date).toLocaleDateString()}</span>
                            </>
                          )}
                          {item.notes && (
                            <span className="text-gray-600">{item.notes}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="text-primary-600 hover:text-primary-900 text-sm font-medium">
                        Update
                      </button>
                      <button className="text-primary-600 hover:text-primary-900 text-sm font-medium">
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  )
}